import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MARKET_REFRESH_MS, playersAPI, tradesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import MarketLockBanner from '../components/MarketLockBanner';
import { PlayerPhoto } from '../components/PlayerCard';
import useMarketStatus from '../hooks/useMarketStatus';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ArrowLeft, TrendingUp, TrendingDown, Lock } from 'lucide-react';

const IST_OFFSET_MS = 330 * 60 * 1000;

function toIstDateParts(dateLike) {
  const d = new Date(new Date(dateLike).getTime() + IST_OFFSET_MS);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
}

function istToUtcMs(year, month, day, hour, minute = 0) {
  return Date.UTC(year, month - 1, day, hour, minute) - IST_OFFSET_MS;
}

function addIstDays(parts, daysToAdd) {
  const utcMs = istToUtcMs(parts.year, parts.month, parts.day, 12, 0);
  return toIstDateParts(utcMs + daysToAdd * 24 * 60 * 60 * 1000);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem',
    }}>
      <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 700, color: 'var(--accent)' }}>{payload[0].value.toFixed(1)} pts</div>
    </div>
  );
}

function getScoringRates(role, sessionBase) {
  const b = sessionBase || 0;
  const fmt = v => (v >= 0 ? '+' : '') + v.toFixed(2) + ' pts';
  const pct = v => (v >= 0 ? '+' : '') + (v * 100).toFixed(1) + '%';
  const rates = [];
  const isBat = ['batsman', 'all-rounder', 'wicket-keeper'].includes(role);
  const isBowl = ['bowler', 'all-rounder'].includes(role);

  if (isBat) {
    rates.push({ label: 'Runs', detail: '≈ logarithmic up to +24%',      example: `e.g. 30 runs → ${fmt(b * 0.24 * (1 - Math.exp(-30 / 34)))}` });
    rates.push({ label: 'Each Four (×4)',  detail: '+0.6% per boundary',   example: `e.g. 3 fours → ${fmt(b * 0.018)}` });
    rates.push({ label: 'Each Six (×6)',   detail: '+1.4% per six',        example: `e.g. 2 sixes → ${fmt(b * 0.028)}` });
    rates.push({ label: 'Strike Rate',     detail: 'vs benchmark SR 125',  example: `SR 150 → ${fmt(b * 0.025)}  ·  SR 100 → ${fmt(b * (-0.025))}` });
    rates.push({ label: '50-run bonus',    detail: '+3% at 50, +5% at 75, +8% at 100', example: `50 runs → ${fmt(b * 0.03)}` });
    rates.push({ label: 'Golden duck',       detail: '−13% — out on 1st ball',           example: `${fmt(b * -0.13)}` });
    rates.push({ label: 'Duck',               detail: '−10% — 0 runs (any dismissal)',     example: `${fmt(b * -0.10)}` });
    rates.push({ label: 'Extreme blocking',   detail: '−12% — 25+ balls, SR < 60',        example: `${fmt(b * -0.12)}` });
    rates.push({ label: 'Slow innings',       detail: '−8% — 20+ balls, SR < 80, <20 runs', example: `${fmt(b * -0.08)}` });
    rates.push({ label: 'Low output',         detail: '−5% — 10+ balls, <10 runs',          example: `${fmt(b * -0.05)}` });
    rates.push({ label: 'Below-par SR',       detail: '−4% — 18+ balls, SR < 95',          example: `${fmt(b * -0.04)}` });
  }
  if (isBowl) {
    rates.push({ label: 'Wickets',         detail: '≈ logarithmic up to +30%', example: `e.g. 2 wkts → ${fmt(b * 0.30 * (1 - Math.exp(-2 / 1.8)))}` });
    rates.push({ label: 'Economy',         detail: 'vs benchmark 7.2',     example: `Eco 6.0 → ${fmt(b * 0.0216)}  ·  Eco 9.5 → ${fmt(b * (-0.0414))}` });
    rates.push({ label: '3-wkt bonus',     detail: '+3.5% for 3 wickets',  example: fmt(b * 0.035) });
    rates.push({ label: '4+-wkt bonus',    detail: '+6% for 4+ wickets',   example: fmt(b * 0.06) });
    rates.push({ label: 'Hammered (no wkts)', detail: '−13% — Eco > 13, 0 wkts (2+ overs)', example: `${fmt(b * -0.13)}` });
    rates.push({ label: 'Expensive wicketless', detail: '−4.5%–−9% — Eco > 9.2, 0 wkts', example: `Eco 11 → ${fmt(b * -0.09)}` });
    rates.push({ label: 'Expensive + wickets',  detail: '−4% — Eco > 12 even with wkts',  example: fmt(b * -0.04) });
  }
  rates.push({ label: 'Each Catch',        detail: '+1.2% per catch (max +4%)', example: `e.g. 2 catches → ${fmt(b * 0.024)}` });
  return rates;
}

function computePriceBreakup(player, stats) {
  const role = player.role;
  const runs = Number(stats?.runs || 0);
  const ballsFaced = Number(stats?.balls || 0);
  const wickets = Number(stats?.wickets || 0);
  const oversBowled = Number(stats?.overs || 0);
  const runsGiven = Number(stats?.runsGiven || 0);
  const catches = Number(stats?.catches || 0);
  const fours = Number(stats?.fours || 0);
  const sixes = Number(stats?.sixes || 0);

  const seasonBase = parseFloat(player.base_price || 0);
  const sessionBase = parseFloat(player.day_open_price || player.current_stock_price || player.base_price || 0);

  const careerSr = parseFloat(player.career_sr || 120);
  const careerEco = parseFloat(player.career_economy || 8);
  const strikeRate = ballsFaced > 5 ? (runs / ballsFaced) * 100 : careerSr;
  const economy = oversBowled > 0 ? (runsGiven / oversBowled) : careerEco;

  let multiplier = 1.0;
  const parts = [];

  // detail: human-readable stat description shown under row label
  const add = (label, value, detail = '', enabled = true) => {
    if (!enabled || !Number.isFinite(value) || Math.abs(value) < 0.0001) return;
    multiplier += value;
    parts.push({ label, value, detail, pts: sessionBase * value });
  };

  if (['batsman', 'all-rounder', 'wicket-keeper'].includes(role)) {
    const batVal = 0.24 * (1 - Math.exp(-runs / 34));
    add('Batting – Runs', batVal,
      `${runs} run${runs !== 1 ? 's' : ''} scored`,
      runs > 0);

    if (ballsFaced > 5) {
      const srDelta = (strikeRate - 125) / 100;
      const clampedSr = Math.max(-0.09, Math.min(0.09, srDelta));
      add('Strike Rate',
        clampedSr,
        `SR ${strikeRate.toFixed(0)} vs benchmark 125`);
    }

    if (fours > 0 || sixes > 0) {
      const bVal = Math.min(0.06, (0.006 * fours) + (0.014 * sixes));
      const fPart = fours > 0 ? `${fours}×4 (+${(0.006 * fours * 100).toFixed(1)}%)` : '';
      const sPart = sixes > 0 ? `${sixes}×6 (+${(0.014 * sixes * 100).toFixed(1)}%)` : '';
      add('Boundaries',
        bVal,
        [fPart, sPart].filter(Boolean).join('  ·  '));
    }

    if (runs >= 100) add('Milestone', 0.08, '100+ runs bonus');
    else if (runs >= 75) add('Milestone', 0.05, '75+ runs bonus');
    else if (runs >= 50) add('Milestone', 0.03, '50+ runs bonus');
    else if (runs >= 30) add('Milestone', 0.015, '30+ runs bonus');

    if (ballsFaced >= 1 && runs === 0) {
      if (ballsFaced === 1)
        add('Golden duck', -0.13, 'Out on the very first ball');
      else
        add('Duck penalty', -0.10, `0 off ${ballsFaced} balls`);
    } else if (ballsFaced >= 25 && strikeRate < 60) {
      add('Extreme blocking', -0.12, `SR ${strikeRate.toFixed(0)} off ${ballsFaced} balls — crippled T20 innings`);
    } else if (ballsFaced >= 20 && strikeRate < 80 && runs < 20) {
      add('Slow innings penalty', -0.08, `${runs} off ${ballsFaced} balls (SR ${strikeRate.toFixed(0)})`);
    } else if (ballsFaced >= 10 && runs < 10) {
      add('Low-score penalty', -0.05, `${runs} off ${ballsFaced} balls`);
    } else if (ballsFaced >= 18 && strikeRate < 95) {
      add('Slow SR penalty', -0.04, `SR ${strikeRate.toFixed(0)} below 95`);
    }
  }

  if (['bowler', 'all-rounder'].includes(role)) {
    if (wickets > 0) {
      const wVal = 0.30 * (1 - Math.exp(-wickets / 1.8));
      add('Wickets', wVal,
        `${wickets} wkt${wickets !== 1 ? 's' : ''} taken`);
    }

    if (oversBowled >= 1.5) {
      const ecoAdj = (7.2 - economy) * 0.018;
      const clampedEco = Math.max(-0.11, Math.min(0.08, ecoAdj));
      add('Economy',
        clampedEco,
        `Eco ${economy.toFixed(2)} vs benchmark 7.2`);
    }

    if (wickets >= 4) add('Bowling haul', 0.06, '4+ wickets bonus');
    else if (wickets >= 3) add('Bowling haul', 0.035, '3 wickets bonus');

    if (oversBowled >= 2 && wickets === 0) {
      if (economy > 13)
        add('Bowling (hammered)', -0.13, `${oversBowled.toFixed(1)} overs · 0 wkts · Eco ${economy.toFixed(2)}`);
      else if (economy > 11)
        add('Bowling penalty', -0.09, `${oversBowled.toFixed(1)} overs · 0 wkts · Eco ${economy.toFixed(2)}`);
      else if (economy > 10)
        add('Bowling penalty', -0.065, `${oversBowled.toFixed(1)} overs · 0 wkts · Eco ${economy.toFixed(2)}`);
      else if (economy > 9.2)
        add('Bowling penalty', -0.045, `${oversBowled.toFixed(1)} overs · 0 wkts · Eco ${economy.toFixed(2)}`);
    } else if (oversBowled >= 2 && wickets > 0 && economy > 12) {
      add('Expensive spell', -0.04, `${oversBowled.toFixed(1)} overs · Eco ${economy.toFixed(2)} despite wickets`);
    }
  }

  // All-rounder compound failure
  if (role === 'all-rounder') {
    const batFailed = (ballsFaced >= 1 && runs === 0) ||
                      (ballsFaced >= 10 && runs < 10) ||
                      (ballsFaced >= 20 && strikeRate < 80);
    const bowlFailed = (oversBowled >= 2 && wickets === 0 && economy > 9.2) ||
                       (oversBowled >= 2 && economy > 12);
    if (batFailed && bowlFailed)
      add('All-rounder double failure', -0.06, 'Failed with both bat and ball');
  }

  if (catches > 0) {
    const cVal = Math.min(0.04, 0.012 * catches);
    add('Fielding', cVal, `${catches} catch${catches !== 1 ? 'es' : ''}`);
  }

  let rawPrice = sessionBase * multiplier;
  let cappedPrice = Math.min(sessionBase * 1.32, Math.max(sessionBase * 0.62, rawPrice));
  cappedPrice = Math.max(seasonBase * 0.60, cappedPrice);
  cappedPrice = Math.min(cappedPrice, 1999.0);

  return {
    sessionBase,
    strikeRate,
    economy,
    parts,
    multiplier,
    rawPrice,
    cappedPrice: Math.round(cappedPrice * 100) / 100,
  };
}

export default function PlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateAfterTrade } = useAuth();
  const [player, setPlayer] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [trading, setTrading] = useState(false);
  const { status: marketStatus } = useMarketStatus();

  const fetchData = useCallback(async () => {
    try {
      const [pRes, portRes] = await Promise.all([
        playersAPI.getOne(id),
        playersAPI.getPortfolio(),
      ]);
      setPlayer(pRes.data);
      setPortfolio(portRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, MARKET_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const holding = portfolio.find(h => h.player_id === parseInt(id));

  const handleTrade = async (type) => {
    if (marketStatus.isLive) {
      setToast({ msg: 'Trading is paused during a live match', type: 'error' });
      return;
    }
    setTrading(true);
    try {
      const r = type === 'buy'
        ? await tradesAPI.buy(parseInt(id))
        : await tradesAPI.sell(parseInt(id));
      updateAfterTrade(r.data);
      setToast({ msg: r.data.message, type: 'success' });
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Trade failed';
      setToast({ msg, type: 'error' });
    } finally {
      setTrading(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!player) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
      Player not found
    </div>
  );

  const teamColor = player.team_color || '#3b82f6';
  const changePct = parseFloat(player.price_change_pct || 0);
  const pnl = holding ? parseFloat(player.current_stock_price) - parseFloat(holding.buy_price) : null;

  const nowIst = toIstDateParts(Date.now());
  const tradingDay = (nowIst.hour < 1 || (nowIst.hour === 1 && nowIst.minute <= 30))
    ? addIstDays(nowIst, -1)
    : nowIst;

  const windowStartMs = istToUtcMs(tradingDay.year, tradingDay.month, tradingDay.day, 14, 30);
  const nextDay = addIstDays(tradingDay, 1);
  const windowEndMs = istToUtcMs(nextDay.year, nextDay.month, nextDay.day, 1, 30);

  const chartData = (player.priceHistory || [])
    .filter(h => {
      const ms = new Date(h.recorded_at).getTime();
      return ms >= windowStartMs && ms <= windowEndMs;
    })
    .map(h => ({
      ts: new Date(h.recorded_at).getTime(),
      time: new Date(h.recorded_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      price: parseFloat(h.price),
    }))
    .sort((a, b) => a.ts - b.ts);

  // Fall back to all available history if current-day window has no data
  const displayChartData = chartData.length > 1
    ? chartData
    : (player.priceHistory || []).map(h => ({
        ts: new Date(h.recorded_at).getTime(),
        time: new Date(h.recorded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
        price: parseFloat(h.price),
      })).sort((a, b) => a.ts - b.ts);

  const dayBase = parseFloat(player.day_open_price || player.base_price || player.current_stock_price || 0);
  if (dayBase > 0 && (chartData.length === 0 || Math.abs(chartData[0].ts - windowStartMs) > 60000)) {
    chartData.unshift({
      ts: windowStartMs,
      time: '02:30 PM',
      price: dayBase,
    });
  }

  const buyPrice = holding ? parseFloat(holding.buy_price) : null;

  const srLive = player.live_balls_faced > 0
    ? ((player.live_runs / player.live_balls_faced) * 100).toFixed(1)
    : null;
  const economy = player.live_overs_bowled > 0
    ? (player.live_runs_given / player.live_overs_bowled).toFixed(2)
    : null;

  // isPlayingToday: player's team is in today's live match → show LIVE badge
  const isPlayingToday = !!player.is_playing_today;
  // liveNow: player has actual live stat data to display (has batted/bowled this match)
  const liveNow = !!marketStatus?.isLive && isPlayingToday && (
    player.live_runs > 0 || player.live_balls_faced > 0 ||
    player.live_wickets > 0 || player.live_overs_bowled > 0 ||
    player.live_catches > 0
  );
  const last = player.lastMatchStats || null;
  const hasLastMatchNumbers = !!last && (
    (last.runs !== null && last.runs !== undefined) ||
    (last.wickets !== null && last.wickets !== undefined) ||
    (last.balls !== null && last.balls !== undefined) ||
    (last.overs !== null && last.overs !== undefined) ||
    (last.runsGiven !== null && last.runsGiven !== undefined)
  );
  const showLastMatchCard = !!last && (!!last.matchLabel || hasLastMatchNumbers);

  const breakupSource = liveNow
    ? 'LIVE'
    : (showLastMatchCard ? 'LAST MATCH' : 'UNAVAILABLE');

  const breakupStats = liveNow
    ? {
        runs: player.live_runs,
        balls: player.live_balls_faced,
        wickets: player.live_wickets,
        overs: player.live_overs_bowled,
        runsGiven: player.live_runs_given,
        catches: player.live_catches,
        fours: 0,
        sixes: 0,
      }
    : (showLastMatchCard ? {
        runs: last?.runs,
        balls: last?.balls,
        wickets: last?.wickets,
        overs: last?.overs,
        runsGiven: last?.runsGiven,
        catches: last?.catches,
        fours: 0,
        sixes: 0,
      } : {
        runs: 0,
        balls: 0,
        wickets: 0,
        overs: 0,
        runsGiven: 0,
        catches: 0,
        fours: 0,
        sixes: 0,
      });
  const breakup = computePriceBreakup(player, breakupStats);

  const isLocked = marketStatus.isLive;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          cursor: 'pointer', fontSize: '0.85rem', marginBottom: '1rem',
        }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      {isLocked && (
        <MarketLockBanner
          matchLabel={marketStatus.matchLabel}
          lockedAt={marketStatus.lockedAt}
        />
      )}

      {/* Player header */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <PlayerPhoto player={player} size={56} />

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                {player.name}
              </h2>
              {isPlayingToday && (
                <span className="live-badge"><span className="live-dot" /> LIVE</span>
              )}
            </div>
            <div style={{
              fontSize: '0.8rem', color: 'var(--text-secondary)',
              marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ color: teamColor, fontWeight: 600 }}>{player.team_name}</span>
              <span>·</span>
              <span style={{ textTransform: 'capitalize' }}>{player.role.replace('-', ' ')}</span>
              <span>·</span>
              <span>{player.nationality}</span>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 700 }}>
              {parseFloat(player.current_stock_price).toFixed(1)}
              <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}> pts</span>
            </div>
            <div style={{
              fontSize: '0.8rem',
              color: changePct >= 0 ? 'var(--green)' : 'var(--red)',
              display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end',
            }}>
              {changePct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}% today
            </div>
          </div>
        </div>
      </div>

      {/* Career stats */}
      <div className="metrics-row" style={{ marginBottom: 12 }}>
        <div className="metric-card">
          <div className="metric-label">Base Price</div>
          <div className="metric-value">{parseFloat(player.base_price).toFixed(0)} pts</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">IPL Runs</div>
          <div className="metric-value">{(player.career_runs || 0).toLocaleString()}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">IPL Wkts</div>
          <div className="metric-value">{player.career_wickets || 0}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Strike Rate</div>
          <div className="metric-value">{parseFloat(player.career_sr || 0).toFixed(1)}</div>
        </div>
      </div>

      {/* Live match stats */}
      {liveNow && (
        <div className="card" style={{ marginBottom: 12, borderColor: 'rgba(239,68,68,0.3)' }}>
          <div style={{
            fontSize: '0.75rem', color: 'var(--red)', fontWeight: 600,
            marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span className="live-dot" style={{ width: 8, height: 8 }} />
            LIVE MATCH STATS · updates every 5 min
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 10 }}>
            {(player.live_runs > 0 || player.live_balls_faced > 0) && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Runs</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {player.live_runs}
                </div>
                {player.live_balls_faced > 0 && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                    {player.live_balls_faced} balls
                  </div>
                )}
              </div>
            )}
            {srLive && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Live SR</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {srLive}
                </div>
              </div>
            )}
            {player.live_wickets > 0 && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Wickets</div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: '1.4rem',
                  fontWeight: 700, color: 'var(--accent)',
                }}>
                  {player.live_wickets}
                </div>
              </div>
            )}
            {economy && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Economy</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {economy}
                </div>
              </div>
            )}
            {player.live_catches > 0 && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Catches</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {player.live_catches}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Last match stats (shown when no live match) */}
      {!liveNow && showLastMatchCard && (
        <div className="card" style={{ marginBottom: 12, borderColor: 'rgba(59,130,246,0.28)' }}>
          <div style={{
            fontSize: '0.75rem', color: 'var(--blue)', fontWeight: 600,
            marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            LAST MATCH STATS
            {last.matchLabel && <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {last.matchLabel}</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 10 }}>
            {last.runs !== null && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Runs</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700 }}>{last.runs}</div>
                {last.balls !== null && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{last.balls} balls</div>
                )}
              </div>
            )}
            {last.strikeRate !== null && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Strike Rate</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700 }}>{last.strikeRate}</div>
              </div>
            )}
            {last.wickets !== null && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Wickets</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--accent)' }}>{last.wickets}</div>
              </div>
            )}
            {last.economy !== null && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Economy</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700 }}>{last.economy}</div>
              </div>
            )}
          </div>
          {!hasLastMatchNumbers && (
            <div style={{ marginTop: 8, fontSize: '0.74rem', color: 'var(--text-muted)' }}>
              No recorded batting/bowling contribution in the latest completed match.
            </div>
          )}
        </div>
      )}

      {/* Points breakup */}
      {(
        <div className="card" style={{ marginBottom: 12, borderColor: 'rgba(124,58,237,0.25)' }}>
          <div style={{
            fontSize: '0.75rem', color: '#7c3aed', fontWeight: 700,
            marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            POINTS BREAKUP · {breakupSource}
          </div>

          {breakupSource === 'UNAVAILABLE' && (
            <div style={{ marginBottom: 8, fontSize: '0.74rem', color: 'var(--text-muted)' }}>
              Live or last-match performance data is not available yet. Showing neutral baseline breakup.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px', fontSize: '0.78rem' }}>

            {/* Base row */}
            <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 600, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Session open price</div>
            <div style={{ fontWeight: 700, paddingBottom: 6, borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{breakup.sessionBase.toFixed(2)} pts</div>

            {/* Component rows – actual match stats (live/last match) */}
            {breakup.parts.length > 0 && breakup.parts.map((p, i) => (
              <React.Fragment key={i}>
                <div style={{ paddingTop: 6 }}>
                  <div style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{p.label}</div>
                  {p.detail && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 1 }}>{p.detail}</div>
                  )}
                </div>
                <div style={{ paddingTop: 6, textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: p.value >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {p.pts >= 0 ? '+' : ''}{p.pts.toFixed(2)} pts
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {p.value >= 0 ? '+' : ''}{(p.value * 100).toFixed(2)}%
                  </div>
                </div>
              </React.Fragment>
            ))}

            {/* Scoring rates (always shown as a reference guide) */}
            <div style={{
              gridColumn: '1 / -1',
              marginTop: breakup.parts.length > 0 ? 10 : 6,
              paddingTop: breakup.parts.length > 0 ? 10 : 0,
              borderTop: breakup.parts.length > 0 ? '1px dashed var(--border)' : 'none',
              fontSize: '0.7rem',
              color: '#7c3aed',
              fontWeight: 700,
              marginBottom: 6,
            }}>
              SCORING RATES
            </div>
            {getScoringRates(player.role, breakup.sessionBase).map((r, i) => (
              <React.Fragment key={'rate-' + i}>
                <div style={{ paddingBottom: 5 }}>
                  <div style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{r.label}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 1 }}>{r.detail}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 1, fontStyle: 'italic' }}>{r.example}</div>
                </div>
                <div style={{ paddingBottom: 5, textAlign: 'right', alignSelf: 'center' }}>
                  {/* intentionally blank; example already in detail */}
                </div>
              </React.Fragment>
            ))}

            {/* Summary rows */}
            <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 600, marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>Total multiplier</div>
            <div style={{ fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', textAlign: 'right' }}>{breakup.multiplier.toFixed(4)}×</div>

            <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>Model price (capped)</div>
            <div style={{ fontWeight: 700, textAlign: 'right' }}>{breakup.cappedPrice.toFixed(2)} pts</div>

            <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', paddingBottom: 2 }}>Current stock price</div>
            <div style={{ fontWeight: 800, color: 'var(--accent)', textAlign: 'right', paddingBottom: 2 }}>{parseFloat(player.current_stock_price).toFixed(2)} pts</div>
          </div>
        </div>
      )}

      {/* Price chart */}
      {displayChartData.length > 1 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 10,
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Price History {chartData.length <= 1 ? '(recent)' : '(today)'}
          </div>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={displayChartData}>
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#475569' }}
                  tickLine={false} axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#475569' }}
                  tickLine={false} axisLine={false}
                  domain={['auto', 'auto']}
                  width={45}
                  tickFormatter={v => v.toFixed(0)}
                />
                <Tooltip content={<CustomTooltip />} />
                {buyPrice && (
                  <ReferenceLine
                    y={buyPrice}
                    stroke="rgba(245,158,11,0.5)"
                    strokeDasharray="4 4"
                    label={{ value: 'Bought', fill: '#f59e0b', fontSize: 10, position: 'insideTopRight' }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke={changePct >= 0 ? '#10b981' : '#ef4444'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Your position */}
      {holding && (
        <div className="card" style={{
          marginBottom: 12,
          borderColor: pnl >= 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)',
        }}>
          <div style={{
            fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8,
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Your Position
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                Bought @ {parseFloat(holding.buy_price).toFixed(1)} pts
              </div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700,
                color: pnl >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 4,
              }}>
                {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)} pts
                ({parseFloat(holding.pnl_pct || 0).toFixed(1)}%)
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>1 unit</div>
          </div>
        </div>
      )}

      {/* Trade button */}
      <div style={{ display: 'flex', gap: 10 }}>
        {holding ? (
          <button
            className="btn btn-sell"
            style={{ flex: 1, padding: '13px', opacity: isLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={() => handleTrade('sell')}
            disabled={trading || isLocked}
          >
            {isLocked && <Lock size={14} />}
            {trading ? 'Processing...' : isLocked ? 'Trading Paused' : `Sell · ${parseFloat(player.current_stock_price).toFixed(1)} pts`}
          </button>
        ) : (
          <button
            className="btn btn-buy"
            style={{ flex: 1, padding: '13px', opacity: isLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={() => handleTrade('buy')}
            disabled={trading || isLocked}
          >
            {isLocked && <Lock size={14} />}
            {trading ? 'Processing...' : isLocked ? 'Trading Paused' : `Buy · ${parseFloat(player.current_stock_price).toFixed(1)} pts`}
          </button>
        )}
      </div>

      {/* Price formula explainer */}
      <div style={{
        marginTop: '1.5rem', padding: '1rem',
        background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
        fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.7,
      }}>
        <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
          How this player's price moves
        </div>
        {['batsman', 'all-rounder', 'wicket-keeper'].includes(player.role) && (
          <div>Batting: runs (log curve, up to +24%) · strike rate vs 125 benchmark · boundary bonus · milestones at 30/50/75/100</div>
        )}
        {['batsman', 'all-rounder', 'wicket-keeper'].includes(player.role) && (
          <div style={{ color: 'var(--red)', marginTop: 2 }}>Penalties: golden duck −13% · duck −10% · extreme blocking (SR&lt;60) −12% · slow innings −8% · low output −5%</div>
        )}
        {['bowler', 'all-rounder'].includes(player.role) && (
          <div>Bowling: wickets (log curve, up to +30%) · economy vs 7.2 benchmark · haul bonus at 3/4 wickets</div>
        )}
        {['bowler', 'all-rounder'].includes(player.role) && (
          <div style={{ color: 'var(--red)', marginTop: 2 }}>Penalties: hammered (Eco&gt;13, 0 wkts) −13% · expensive wicketless −4.5%–−9% · post-match permanent decay</div>
        )}
        {player.role === 'all-rounder' && (
          <div style={{ color: 'var(--red)', marginTop: 2 }}>Double failure bonus penalty: −6% extra if bad in both bat &amp; ball</div>
        )}
        <div style={{ marginTop: 4 }}>Catches: +1.2% each · all prices capped at ±32% of day-open · floor at 60% of season base</div>
      </div>
    </div>
  );
}
